import { makeObservable, observable, action, computed } from 'mobx';
import { useApi} from '/@/app/Api';
import { userFlowState } from '/@/app/states/userFlowState';
import { route } from '/@/app/routes';


class QuestionChoice {
    // Identifier:
    permanentId = 0;
    caption = '';
    answer = null;

    constructor(data) {
        this.permanentId = data.permanent_id;
        this.caption = data.caption;
        this.answer = data.answer;

        makeObservable(this, {
            answer: observable,
            setAnswer: action,
        });
    }
    setAnswer = (answer) => {
        this.answer = answer;
    }
}

class Question {
    api=null;
    // Identifier:
    panelId = 0;
    permanentId = 0;
    // Observables
    caption = '';
    soundLink = '';
    prefix = null;
    answer = null;
    choices = [];
    recalculatesVisibility = false;
    // Current state trigger
    iWantToBecomeCurrent = 0;
    // UI states
    navCurrent = false;
    savingState = false;

    constructor(data, api) {
	this.api=api;
        this.panelId = data.panel_id;
        this.groupId = data.group_id;
        this.type = data.type;  // "common" or "single_choice_group"
        this.caption = data.caption;
        this.soundLink = data.sound_link;
        this.recalculatesVisibility = data.recalculates_visibility;

        switch (this.type) {
            case "common":
                this.answer = data.answer;
                this.permanentId = data.permanent_id;
                break;
            case "single_choice_group":
                this.answer = data.answer;  // for groups, this is just a mark that it was answered
                this.permanentId = `group__${this.groupId}`;
                this.choices = data.choices.map((choiceData) => new QuestionChoice(choiceData));
                break;
            default:
                throw new Error(`Unknown question type: ${this.type}`);
        }


        this.nav_prefix = data.nav_prefix;  // always exists, including empty structure.

        makeObservable(this, {
            caption: observable,
            answer: observable,
            choices: observable,
            groupChoice: computed,
            //
            iWantToBecomeCurrent: observable, // always increasing, so triggers reaction
            setIWantToBecomeCurrent: action,
            // UI states
            setAnswer: action,
            saveAnswer: action,
            savingState: observable,
            setSavingState: action,
            doneSaving: action,
            // UI navigation states
            navCurrent: observable,
            setNavCurrent: action,
            unsetNavCurrent: action,
        });
    }

    get groupChoice() {
        const yesAnswered = this.choices.find((choice) => choice.answer && choice.answer.answer === 'yes');
        if (yesAnswered) {
            return yesAnswered.permanentId;
        }
        return null;
    }

    setIWantToBecomeCurrent = () => {
        this.iWantToBecomeCurrent += 1;
    }

    setNavCurrent = () => {
        this.navCurrent = true;
    }
    unsetNavCurrent = () => {
        this.navCurrent = false;
    }

    setAnswer = (answer) => {
        this.answer = answer;
    }
    saveAnswer = (answer) => {
        if (this.savingState) {
            console.log('Prevented duplicated save');
            return;
        }
        this.setSavingState();

        this.api.request(`cabinet/panels/${this.panelId}/answers/${this.permanentId}/`, { answer: answer, }, { method: 'PUT', })
	.then((response) => {
            this.doneSaving();
            // set after successful save
            this.setAnswer({
                answer: answer,
                answered_at: "now"  // will be replaced on server
            });
            if (response.calculate_required) {
                userFlowState.setCalculateRequired({
                    isRequired: true,
                    postponeForLocation: route("/questions/"),
                });
            }
        }).catch((error) => {
            this.doneSaving();
        });
    }
    saveGroup = (choice) => {
        if (this.savingState) {
            console.log('Prevented duplicated save');
            return;
        }
        this.setSavingState();

        this.api.request(`cabinet/panels/${this.panelId}/answers/group/${this.groupId}/`, { choice_permanent_id: choice, }, { method: 'PUT', })
	.then((response) => {
            this.doneSaving();
            // set after successful save
            for (let c of this.choices) {
                c.setAnswer({
                    answer: c.permanentId === choice ? 'yes' : 'no',
                    answered_at: "now"  // will be replaced on server
                });
            }
            this.setAnswer({
                answer: choice !== -1 ? "yes" : "no",  // magic number for "no". FIXME just get response from server
                answered_at: "now"  // will be replaced on server
            });
            if (response.calculate_required) {
                userFlowState.setCalculateRequired({
                    isRequired: true,
                    postponeForLocation: route("/questions/"),
                });
            }
        }).catch((error) => {
            this.doneSaving();
        });
    }

    setSavingState = () => {
        this.savingState = true;
    }
    doneSaving = () => {
        this.savingState = false;
    }
}


class Section {
    api=null;
    name = '';
    order= 0;
    questions = [];
    // UI states
    navOpen = false;

    constructor(data, api) {
	this.api=api;
        makeObservable(this, {
            name: observable,
            questions: observable,
            navOpen: observable,
            countAll: computed,
            countVisible: computed,
            countAnswered: computed,
            countIDontKnow: computed,
            //
            openNav: action,
            closeNav: action,
            //
            toggleNav: action,
        });
        this.name = data.name;
        this.order = data.order;

        this.questions = data.questions.map((questionData) => new Question(questionData,api));
    }

    get countAll() {
        return this.questions.length;
    }
    get countVisible() {
        // all questions returned by API are visible.
        return this.questions.length;
    }
    get countAnswered() {
        return this.questions.filter((question) =>
            question.answer
        ).length;
    }
    get countIDontKnow() {
        return this.questions.filter((question) =>
            question.answer
            && question.answer.answer === 'i_dont_know'
        ).length;
    }

    openNav = () => {
        this.navOpen = true;
    }
    closeNav = () => {
        this.navOpen = false;
    }
    toggleNav = () => {
        if (this.navOpen) {
            this.closeNav();
        } else {
            this.openNav();
        }
    }
}


export class SectionList {
    api=null;
    sections = [];
    // UI states
    preloading = true;

    constructor(panelId,api) {
	this.api=api;
        this.panelId = panelId;
        makeObservable(this, {
            preloading: observable,
            sections: observable,
            replaceSections: action,
            setLoaded: action,
            setLoading: action,
        });
    }

    fetchData = async () => {
        this.setLoading();
        const response = await this.api.request(`cabinet/panels/${this.panelId}/questions/v2/`);
        const data = await response;  // flat list

        // Add nested sections
        this.replaceSections(data);
        this.setLoaded();
    }

    replaceSections = (newSections) => {
        this.sections = newSections.map((sectionData) => new Section(sectionData,this.api));
    }
    setLoading = () => {
        this.preloading = true;
    }
    setLoaded = () => {
        this.preloading = false;
    }

    findByQuestion = (question) => {
        if (!question) {
            return {section: null, questionIndex: null};
        }
        for (let section of this.sections) {
            let counter = 0;
            for (let q of section.questions) {
                if (q.permanentId === question.permanentId) {
                    // assuming each question is only in one section
                    return {section: section, questionIndex: counter};
                }
                counter++;
            }
        }
        return {section: null, questionIndex: null};
    }
}
